import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  Building2,
  Phone,
  Mail,
  MapPin,
  ExternalLink,
  ChevronRight,
  Users,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Filter,
  RefreshCw,
  ArrowUpRight,
  Briefcase,
  MoreVertical
} from 'lucide-react';
import { format } from 'date-fns';
import { API_URL } from '../config/api';

interface LeadsProps {
  token: string;
}

interface Lead {
  id: string;
  businessName: string;
  dba?: string;
  ownerName?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  phone?: string;
  email?: string;
  website?: string;
  naicsCode?: string;
  naicsDescription?: string;
  businessType?: string;
  status: string;
  priority: number;
  score: number;
  researchStatus?: string;
  lastContactedAt?: string;
  contactAttempts: number;
  nextFollowUpAt?: string;
  createdAt: string;
  interactions?: LeadInteraction[];
}

interface LeadInteraction {
  id: string;
  type: string;
  content: string;
  outcome?: string;
  createdAt: string;
}

interface LeadStats {
  total: number;
  byStatus: Record<string, number>;
  byResearchStatus: Record<string, number>;
  recentlyContacted: number;
  needsFollowUp: number;
}

const statusConfig: Record<string, { label: string; color: string; icon: any }> = {
  new: { label: 'New', color: 'bg-gray-100 text-gray-800', icon: Clock },
  researching: { label: 'Researching', color: 'bg-blue-100 text-blue-800', icon: Search },
  qualified: { label: 'Qualified', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  contacted: { label: 'Contacted', color: 'bg-purple-100 text-purple-800', icon: Phone },
  interested: { label: 'Interested', color: 'bg-emerald-100 text-emerald-800', icon: TrendingUp },
  not_interested: { label: 'Not Interested', color: 'bg-red-100 text-red-800', icon: XCircle },
  converted: { label: 'Converted', color: 'bg-indigo-100 text-indigo-800', icon: ArrowUpRight },
  invalid: { label: 'Invalid', color: 'bg-gray-100 text-gray-500', icon: AlertCircle },
};

const priorityColors: Record<number, string> = {
  10: 'bg-red-500',
  9: 'bg-red-400',
  8: 'bg-orange-500',
  7: 'bg-orange-400',
  6: 'bg-yellow-500',
  5: 'bg-yellow-400',
  4: 'bg-green-400',
  3: 'bg-green-300',
  2: 'bg-gray-300',
  1: 'bg-gray-200',
};

export default function Leads({ token }: LeadsProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const queryClient = useQueryClient();

  // Fetch leads
  const { data: leadsData, isLoading } = useQuery<{ leads: Lead[]; total: number }>({
    queryKey: ['leads', searchTerm, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);
      params.append('limit', '100');

      const response = await fetch(`${API_URL}/api/leads?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) return { leads: [], total: 0 };
      return response.json();
    },
  });

  // Fetch stats
  const { data: stats } = useQuery<LeadStats>({
    queryKey: ['leads-stats'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/api/leads/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) return { total: 0, byStatus: {}, byResearchStatus: {}, recentlyContacted: 0, needsFollowUp: 0 };
      return response.json();
    },
  });

  // Update lead mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Lead> }) => {
      const response = await fetch(`${API_URL}/api/leads/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });
      if (!response.ok) throw new Error('Failed to update lead');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['leads-stats'] });
    },
  });

  // Convert to opportunity mutation
  const convertMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`${API_URL}/api/leads/${id}/convert`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) throw new Error('Failed to convert lead');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['leads-stats'] });
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
      setSelectedLead(null);
    },
  });

  const leads = leadsData?.leads || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Lead Pipeline</h1>
          <p className="text-sm text-gray-500 mt-1">
            Pre-qualified prospects from business tax records
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Building2 className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Leads</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.total || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Qualified</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.byStatus?.qualified || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Phone className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Contacted</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.recentlyContacted || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Clock className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Needs Follow-up</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.needsFollowUp || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <ArrowUpRight className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Converted</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.byStatus?.converted || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search businesses, owners, emails..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Statuses</option>
            {Object.entries(statusConfig).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <RefreshCw className="h-8 w-8 animate-spin text-gray-400 mx-auto" />
            <p className="mt-2 text-gray-500">Loading leads...</p>
          </div>
        ) : leads.length === 0 ? (
          <div className="p-8 text-center">
            <Building2 className="h-12 w-12 text-gray-300 mx-auto" />
            <p className="mt-2 text-gray-500">No leads found</p>
            <p className="text-sm text-gray-400">Import leads from a CSV to get started</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Business
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Industry
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {leads.map((lead) => {
                const status = statusConfig[lead.status] || statusConfig.new;
                const StatusIcon = status.icon;

                return (
                  <tr 
                    key={lead.id} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedLead(lead)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-gray-100 rounded-lg">
                          <Building2 className="h-5 w-5 text-gray-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{lead.businessName}</p>
                          {lead.dba && (
                            <p className="text-sm text-gray-500">DBA: {lead.dba}</p>
                          )}
                          {lead.city && (
                            <p className="text-xs text-gray-400 flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {lead.city}, {lead.state}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {lead.ownerName && (
                          <p className="text-sm text-gray-900">{lead.ownerName}</p>
                        )}
                        {lead.phone && (
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {lead.phone}
                          </p>
                        )}
                        {lead.email && (
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {lead.email}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        {lead.naicsCode && (
                          <p className="text-xs font-mono text-gray-500">{lead.naicsCode}</p>
                        )}
                        <p className="text-sm text-gray-700 line-clamp-2">
                          {lead.naicsDescription || lead.businessType || '-'}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                        <StatusIcon className="h-3 w-3" />
                        {status.label}
                      </span>
                      {lead.contactAttempts > 0 && (
                        <p className="text-xs text-gray-400 mt-1">
                          {lead.contactAttempts} attempt{lead.contactAttempts > 1 ? 's' : ''}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-8 rounded-full ${priorityColors[lead.priority] || priorityColors[5]}`} />
                        <span className="text-sm font-medium text-gray-900">{lead.priority}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedLead(lead);
                        }}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Lead Detail Sidebar */}
      {selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-end">
          <div className="bg-white w-full max-w-lg h-full overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">{selectedLead.businessName}</h2>
              <button
                onClick={() => setSelectedLead(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <XCircle className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Business Info */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-500 uppercase">Business Info</h3>
                {selectedLead.dba && (
                  <p className="text-sm"><span className="text-gray-500">DBA:</span> {selectedLead.dba}</p>
                )}
                {selectedLead.address && (
                  <p className="text-sm flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                    <span>
                      {selectedLead.address}<br />
                      {selectedLead.city}, {selectedLead.state} {selectedLead.zipCode}
                    </span>
                  </p>
                )}
                {selectedLead.naicsCode && (
                  <p className="text-sm">
                    <span className="text-gray-500">NAICS:</span>{' '}
                    <span className="font-mono">{selectedLead.naicsCode}</span>
                    {selectedLead.naicsDescription && (
                      <span className="text-gray-600"> - {selectedLead.naicsDescription}</span>
                    )}
                  </p>
                )}
                {selectedLead.businessType && (
                  <p className="text-sm"><span className="text-gray-500">Type:</span> {selectedLead.businessType}</p>
                )}
              </div>

              {/* Contact Info */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-500 uppercase">Contact</h3>
                {selectedLead.ownerName && (
                  <p className="text-sm flex items-center gap-2">
                    <Users className="h-4 w-4 text-gray-400" />
                    {selectedLead.ownerName}
                  </p>
                )}
                {selectedLead.phone && (
                  <a 
                    href={`tel:${selectedLead.phone}`}
                    className="text-sm flex items-center gap-2 text-blue-600 hover:underline"
                  >
                    <Phone className="h-4 w-4" />
                    {selectedLead.phone}
                  </a>
                )}
                {selectedLead.email && (
                  <a 
                    href={`mailto:${selectedLead.email}`}
                    className="text-sm flex items-center gap-2 text-blue-600 hover:underline"
                  >
                    <Mail className="h-4 w-4" />
                    {selectedLead.email}
                  </a>
                )}
                {selectedLead.website && (
                  <a 
                    href={selectedLead.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm flex items-center gap-2 text-blue-600 hover:underline"
                  >
                    <ExternalLink className="h-4 w-4" />
                    {selectedLead.website}
                  </a>
                )}
              </div>

              {/* Status Update */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-500 uppercase">Update Status</h3>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(statusConfig).map(([key, { label, color }]) => (
                    <button
                      key={key}
                      onClick={() => updateMutation.mutate({ id: selectedLead.id, updates: { status: key } })}
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        selectedLead.status === key ? color : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-500 uppercase">Actions</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => convertMutation.mutate(selectedLead.id)}
                    disabled={selectedLead.status === 'converted' || convertMutation.isPending}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <ArrowUpRight className="h-4 w-4" />
                    Convert to Opportunity
                  </button>
                </div>
              </div>

              {/* Interactions */}
              {selectedLead.interactions && selectedLead.interactions.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-500 uppercase">Recent Activity</h3>
                  <div className="space-y-2">
                    {selectedLead.interactions.map((interaction) => (
                      <div key={interaction.id} className="bg-gray-50 rounded-lg p-3">
                        <div className="flex justify-between items-start">
                          <span className="text-xs font-medium text-gray-500 uppercase">
                            {interaction.type}
                          </span>
                          <span className="text-xs text-gray-400">
                            {format(new Date(interaction.createdAt), 'MMM d, h:mm a')}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 mt-1">{interaction.content}</p>
                        {interaction.outcome && (
                          <span className="inline-block mt-1 text-xs bg-gray-200 px-2 py-0.5 rounded">
                            {interaction.outcome}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
